import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import BoyIcon from '@mui/icons-material/Boy';
import GroupIcon from '@mui/icons-material/Group';
import PersonAvatar from '../../../appcomponents/events/shared/PersonAvatar';

/**
 * EventHeader - Displays event header with person info, images, and accuracy
 * @param {string} personImage - URL of person's reference image
 * @param {string} personName - Person's name
 * @param {string} username - Username (displayed under person name)
 * @param {string} matchSource - Match source ('face_match' or 'human_match')
 * @param {string} capturedImageSignedUrl - Signed URL of captured image (for AI matches)
 * @param {string} capturedImageUrl - S3 key/URL of captured image (to detect multi-face)
 * @param {boolean} loadingCapturedImage - Whether captured image is loading
 * @param {number} matchAccuracy - Match accuracy (0-1)
 * @param {string} eventLabel - Event type label
 * @param {string} personUri - URI to person's profile
 * @param {number} matchCount - Number of faces matched in image
 * @param {number} detectedFaceCount - Total number of faces detected in image
 * @param {Object} boundingBox - Bounding box data {Width, Height, Left, Top}
 * @param {Function} onPersonImageClick - Callback when person image is clicked
 * @param {Function} onCapturedImageClick - Callback when captured image is clicked
 */
export default function EventHeader({
  personImage,
  personName,
  username,
  matchSource,
  capturedImageSignedUrl,
  capturedImageUrl,
  loadingCapturedImage,
  matchAccuracy,
  eventLabel,
  personUri,
  matchCount,
  detectedFaceCount,
  boundingBox,
  onPersonImageClick,
  onCapturedImageClick
}) {
  const [showEnlarged, setShowEnlarged] = React.useState(false);
  const canvasRef = React.useRef(null);
  const imageRef = React.useRef(null);

  // Detect if this is a multi-face capture
  const isMultiFace = React.useMemo(() => {
    if (!capturedImageUrl) return false;
    const filename = capturedImageUrl.split('/').pop();
    const withoutExt = filename.replace(/\.(jpg|jpeg|png)$/i, '');
    const parts = withoutExt.split('_');
    return parts.length > 2; // More than timestamp + 1 person ID = multi-face
  }, [capturedImageUrl]);

  // Handle captured image click - toggle enlarged view
  const handleCapturedImageClick = React.useCallback(() => {
    setShowEnlarged(prev => !prev);
  }, []);

  // Draw bounding box on canvas when image loads
  const drawImageWithBoundingBox = React.useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const img = imageRef.current;

    // Wait for image to be loaded
    if (!img.complete || img.naturalWidth === 0) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size to match image natural dimensions
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // Draw the image
    ctx.drawImage(img, 0, 0);

    // Draw bounding box if available
    if (boundingBox) {
      const { Width, Height, Left, Top } = boundingBox;
      const boxX = Left * img.naturalWidth;
      const boxY = Top * img.naturalHeight;
      const boxWidth = Width * img.naturalWidth;
      const boxHeight = Height * img.naturalHeight;

      // Draw rectangle
      ctx.strokeStyle = '#2196f3';
      ctx.lineWidth = 3;
      ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

      // Draw corner indicators
      const cornerSize = 15;
      ctx.fillStyle = '#2196f3';

      // Top-left
      ctx.fillRect(boxX - 1.5, boxY - 1.5, cornerSize, 3);
      ctx.fillRect(boxX - 1.5, boxY - 1.5, 3, cornerSize);

      // Top-right
      ctx.fillRect(boxX + boxWidth - cornerSize + 1.5, boxY - 1.5, cornerSize, 3);
      ctx.fillRect(boxX + boxWidth - 1.5, boxY - 1.5, 3, cornerSize);

      // Bottom-left
      ctx.fillRect(boxX - 1.5, boxY + boxHeight - 1.5, cornerSize, 3);
      ctx.fillRect(boxX - 1.5, boxY + boxHeight - cornerSize + 1.5, 3, cornerSize);

      // Bottom-right
      ctx.fillRect(boxX + boxWidth - cornerSize + 1.5, boxY + boxHeight - 1.5, cornerSize, 3);
      ctx.fillRect(boxX + boxWidth - 1.5, boxY + boxHeight - cornerSize + 1.5, 3, cornerSize);
    }
  }, [boundingBox]);

  // Trigger drawing when enlarged view opens or image changes
  React.useEffect(() => {
    if (showEnlarged && capturedImageSignedUrl) {
      // Small delay to ensure image is loaded
      const timer = setTimeout(drawImageWithBoundingBox, 50);
      return () => clearTimeout(timer);
    }
  }, [showEnlarged, capturedImageSignedUrl, drawImageWithBoundingBox]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {/* Match indicator with accuracy circle */}
      {matchSource === 'face_match' && matchAccuracy && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0
        }}>
          {/* AI match icon (above circle) */}
          <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20 }} />

          {/* Accuracy circle (50% smaller: 56px → 28px) */}
          <Box sx={{
            position: 'relative',
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `conic-gradient(${
              matchAccuracy >= 1.0 ? '#2e7d32' :
              matchAccuracy >= 0.9 ? '#2e7d32' :
              matchAccuracy >= 0.8 ? '#c77700' :
              '#b71c1c'
            } ${matchAccuracy * 360}deg, rgba(0, 0, 0, 0.1) 0deg)`
          }}>
            <Box sx={{
              width: 23,
              height: 23,
              borderRadius: '50%',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.5rem',
                  color:
                    matchAccuracy >= 1.0 ? '#2e7d32' :
                    matchAccuracy >= 0.9 ? '#2e7d32' :
                    matchAccuracy >= 0.8 ? '#c77700' :
                    '#b71c1c',
                  lineHeight: 1
                }}
              >
                {matchAccuracy >= 1.0
                  ? '100'
                  : `${(matchAccuracy * 100).toFixed(0)}`
                }
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Manual match indicator */}
      {(matchSource === 'human_match' || matchSource === 'manual_filter') && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0
        }}>
          {/* Manual match icon (above circle) */}
          <BoyIcon sx={{ color: matchSource === 'manual_filter' ? '#c77700' : '#2e7d32', fontSize: 20 }} />

          {/* Manual indicator circle (50% smaller: 56px → 28px) */}
          <Box sx={{
            position: 'relative',
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `conic-gradient(${matchSource === 'manual_filter' ? '#c77700' : '#2e7d32'} 360deg, rgba(0, 0, 0, 0.1) 0deg)`
          }}>
            <Box sx={{
              width: 23,
              height: 23,
              borderRadius: '50%',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  color: matchSource === 'manual_filter' ? '#c77700' : '#2e7d32',
                  lineHeight: 1
                }}
              >
                ?
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Images container: Reference image + Captured image/Boy icon */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        {/* Person avatar/reference image */}
        <Box
          onClick={() => {
            if (matchSource === 'face_match' && capturedImageSignedUrl) {
              onPersonImageClick();
            } else if (personUri) {
              window.open(personUri, '_blank', 'noreferrer');
            }
          }}
          sx={{
            cursor: (matchSource === 'face_match' && capturedImageSignedUrl) || personUri ? 'pointer' : 'default',
            '&:hover': (matchSource === 'face_match' && capturedImageSignedUrl) || personUri ? {
              opacity: 0.8
            } : {}
          }}
        >
          <PersonAvatar imageUrl={personImage} name={personName} size={60} />
        </Box>

        {/* Captured image (for AI matches with image URL) */}
        {matchSource === 'face_match' && (loadingCapturedImage || capturedImageSignedUrl) && (
          <>
            {loadingCapturedImage ? (
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                border: '2px solid rgba(25, 118, 210, 0.3)'
              }}>
                <Typography variant="caption">...</Typography>
              </Box>
            ) : (
              <Box
                component="img"
                src={capturedImageSignedUrl}
                alt="Captured"
                onClick={handleCapturedImageClick}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  objectFit: 'cover',
                  border: showEnlarged ? '2px solid rgba(25, 118, 210, 1)' : '2px solid rgba(25, 118, 210, 0.5)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 2
                  }
                }}
              />
            )}
          </>
        )}

        {/* Person icon in bounding box (for filtered/manual matches) */}
        {(matchSource === 'human_match' || matchSource === 'manual_filter') && (
          <Box sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed',
            borderColor: matchSource === 'manual_filter' ? '#c77700' : '#2e7d32',
            backgroundColor: matchSource === 'manual_filter' ? 'rgba(199, 119, 0, 0.05)' : 'rgba(46, 125, 50, 0.05)'
          }}>
            <PersonIcon sx={{
              fontSize: 36,
              color: matchSource === 'manual_filter' ? '#c77700' : '#2e7d32'
            }} />
          </Box>
        )}
      </Box>

      {/* Person name, username, and event type */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {personName || 'Unknown'}
          </Typography>
          {isMultiFace && (
            <GroupIcon
              sx={{
                color: 'primary.main',
                fontSize: 20
              }}
            />
          )}
          {matchCount !== null && matchCount !== undefined && detectedFaceCount !== null && detectedFaceCount !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {matchCount}/{detectedFaceCount}
            </Typography>
          )}
        </Box>
        {username && (
          <Typography variant="body2" color="text.secondary">
            {username}
          </Typography>
        )}
        {eventLabel && (
          <Typography variant="body2" color="text.secondary">
            {eventLabel}
          </Typography>
        )}
      </Box>
    </Box>

      {/* Enlarged image with bounding box */}
      {showEnlarged && capturedImageSignedUrl && (
        <Box sx={{ width: '100%', mt: 1 }}>
          {/* Hidden image for loading */}
          <img
            ref={imageRef}
            src={capturedImageSignedUrl}
            alt="Reference for canvas"
            style={{ display: 'none' }}
            onLoad={drawImageWithBoundingBox}
          />

          {/* Canvas with bounding box */}
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              border: '2px solid rgba(25, 118, 210, 0.5)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              display: 'block'
            }}
          />
        </Box>
      )}
    </Box>
  );
}
